import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, Ref, computed, WritableComputedRef, PropType } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    VueFlowNodeModel,
    MultiSelectOption,
    QueueModel,
} from '@/types';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { ReplyIcon } from '@heroicons/vue/outline';
import { required } from '@/utils/validators';
import { useI18n } from 'vue-i18n';
import store from '@/store';
import { helpers } from '@vuelidate/validators';

interface ITransferFormState {
    queue: null | QueueModel;
    whisper: null | string;
    queueTimeoutSeconds: null | number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TransferToQueue',
  props: {
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
},
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = useI18n();

const props = __props;

const showForm = ref(false);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const queueOptions = computed(
    () => store.getters['flow/' + ConnectFlowModelingGetters.QUEUE_OPTIONS],
);
const formState: Ref<ITransferFormState> = ref({
    queue: null,
    whisper: null,
    queueTimeoutSeconds: null,
});

const queueTimeoutValidationText = t('connectFlow.node.transferToQueue.queueTimeout.validation');
const validQueueTimeout = helpers.withMessage(queueTimeoutValidationText, (value) => {
    if (value === null || value === '' || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    const n = Number(value);
    return Number.isInteger(n) && n >= 5 && n <= 60;
});

const rules = computed<ValidationArgs<ITransferFormState>>(() => {
    const r: ValidationArgs<ITransferFormState> = {
        queue: {
            required,
        },
        whisper: {},
        queueTimeoutSeconds: { validQueueTimeout },
    };
    return r;
});
const v$: Ref<Validation<ValidationArgs<ITransferFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.transferToQueue.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    } else if (nodeWritable.value.data.textToAudio) {
        text = t('connectFlow.node.transferToQueue.textToAudio');
    }
    return text;
});

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}
async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value) {
        nodeWritable.value.data.related = {
            value: formState.value.queue?.id,
            label: formState.value.queue?.name,
        } as MultiSelectOption;
        nodeWritable.value.data.whisper = formState.value.whisper ?? undefined;
        nodeWritable.value.data.queueTimeoutSeconds =
            formState.value.queueTimeoutSeconds ?? undefined;
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}

onMounted(() => {
    if (nodeWritable.value.data.related !== null) {
        formState.value.queue = queueOptions.value.find(
            (queue: QueueModel) => queue.id == nodeWritable.value.data.related?.value,
        );
    }

    if (nodeWritable.value.data.whisper !== undefined) {
        formState.value.whisper = nodeWritable.value.data.whisper;
    }

    if (nodeWritable.value.data.queueTimeoutSeconds !== undefined) {
        formState.value.queueTimeoutSeconds = nodeWritable.value.data.queueTimeoutSeconds;
    }

    v$.value.$validate().then((result) => {
        isFormCorrect.value = result;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    });
});

const __returned__ = { t, props, showForm, nodeWritable, queueOptions, formState, queueTimeoutValidationText, validQueueTimeout, rules, v$, nodeText, isFormCorrect, removeNode, onSubmit, get ReplyIcon() { return ReplyIcon } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})